import React from "react";
import {
  Settings,
  Bell,
  Moon,
  Sun,
  Globe,
  Shield,
  Palette,
  Database,
  Mail,
  Smartphone,
  Lock,
  Eye,
  EyeOff,
  RefreshCw,
  Download,
  Upload,
  Trash2,
  HelpCircle,
  ChevronDown,
  LogOut,
} from "lucide-react";
import { useSettingsState } from "../hooks/useSettingsState";
import { getSettingsStyles } from "../styles/settingsStyles";
import { exportSettings } from "../utils/settingsExporter";
import { useLogout } from "../../../hooks/useLogout";

const EnhancedSettingsPage = () => {
  const { logout } = useLogout();           // ← get logout from hook
  const {
    theme,
    setTheme,
    language,
    toggleLanguage,
    t,
    isRTL,
    isMobile,
    notifications,
    privacy,
    display,
    security,
    savedStatus,
    showPasswordSettings,
    setShowPasswordSettings,
    showLogoutModal,
    setShowLogoutModal,
    handleNotificationChange,
    handlePrivacyChange,
    handleDisplayChange,
    handleSecurityChange,
    handleLogout,
    confirmLogout,
  } = useSettingsState({ onLogout: logout });


  const styles = getSettingsStyles({ theme, isMobile, isRTL });

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.headerLeft}>
            <div style={styles.iconWrapper}>
              <Settings
                size={isMobile ? 28 : 36}
                color="#ffffff"
                strokeWidth={2.5}
              />
            </div>
            <div style={styles.titleWrapper}>
              <h1 style={styles.title}>{t.title}</h1>
              {/*<p style={styles.subtitle}>{t.subtitle}</p>*/}
            </div>
          </div>

          {/* Logout Button */}
          <div
            style={{
              ...styles.settingItem,
              justifyContent: isMobile
                ? "center"
                : isRTL
                ? "flex-start"
                : "flex-end",
              paddingTop: "16px",
              borderBottom: "none",
            }}
          >
            <button
              onClick={handleLogout}
              style={styles.logoutButton}
              title={t.logout}
            >
              <LogOut size={16} />
              <span style={{ fontWeight: 800 }}>{t.logout}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Logout Modal */}
      {showLogoutModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.85)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            backdropFilter: "blur(10px)",
            padding: isMobile ? "20px" : "40px",
          }}
          onClick={() => setShowLogoutModal(false)}
        >
          <div
            style={{
              background:
                theme === "light"
                  ? "linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(249, 250, 251, 0.98) 100%)"
                  : "linear-gradient(135deg, rgba(30, 41, 59, 0.98) 0%, rgba(15, 23, 42, 0.98) 100%)",
              border:
                theme === "light"
                  ? "2px solid rgba(226, 232, 240, 0.8)"
                  : "2px solid rgba(71, 85, 105, 0.4)",
              borderRadius: isMobile ? "20px" : "28px",
              padding: isMobile ? "24px" : "40px",
              maxWidth: "600px",
              width: "100%",
              backdropFilter: "blur(24px)",
              boxShadow:
                theme === "light"
                  ? "0 25px 50px -12px rgba(0, 0, 0, 0.25)"
                  : "0 25px 50px -12px rgba(0, 0, 0, 0.7)",
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              gap: "24px",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "center",
                flexDirection: isMobile ? "column" : "row",
              }}
            >
              <button
                onClick={confirmLogout}
                style={{
                  background:
                    "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                  color: "white",
                  border: "none",
                  borderRadius: "14px",
                  padding: isMobile ? "14px 28px" : "12px 32px",
                  fontWeight: "700",
                  fontSize: isMobile ? "15px" : "16px",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  flex: isMobile ? "none" : 1,
                }}
              >
                {isRTL ? "تأكيد الخروج" : "Confirm Logout"}
              </button>

              <button
                onClick={() => setShowLogoutModal(false)}
                style={{
                  background:
                    theme === "light"
                      ? "rgba(226, 232, 240, 0.8)"
                      : "rgba(71, 85, 105, 0.5)",
                  color: theme === "light" ? "#1e293b" : "#ffffff",
                  border: "none",
                  borderRadius: "14px",
                  padding: isMobile ? "14px 28px" : "12px 32px",
                  fontWeight: "700",
                  fontSize: isMobile ? "15px" : "16px",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  flex: isMobile ? "none" : 1,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = "0.8";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = "1";
                }}
              >
                {isRTL ? "إلغاء" : "Cancel"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={styles.settingsGrid}>
        {/* Language Card */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>
              <Globe size={isMobile ? 18 : 22} />
              {t.language.title}
            </h3>
            <p style={styles.cardDescription}>{t.language.description}</p>
          </div>
          <div style={styles.settingGroup}>
            <div style={styles.settingItem}>
              <label style={styles.settingLabel}>
                <div style={styles.labelContent}>
                  <span style={styles.labelTitle}>
                    {t.language.displayLanguage}
                  </span>
                  <span style={styles.labelDescription}>
                    {t.language.languageDescription}
                  </span>
                </div>
              </label>
              <div style={styles.themeToggle}>
                <button
                  style={{
                    ...styles.themeButton,
                    ...(language === "en" ? styles.themeButtonActive : {}),
                  }}
                  onClick={() =>
                    toggleLanguage({ target: { value: "en" } })
                  }
                >
                  <Globe size={isMobile ? 16 : 18} />
                  {t.language.english}
                </button>
                <button
                  style={{
                    ...styles.themeButton,
                    ...(language === "ar" ? styles.themeButtonActive : {}),
                  }}
                  onClick={() =>
                    toggleLanguage({ target: { value: "ar" } })
                  }
                >
                  <Globe size={isMobile ? 16 : 18} />
                  {t.language.arabic}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Appearance Card */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>
              <Palette size={isMobile ? 18 : 22} />
              {t.appearance.title}
            </h3>
            <p style={styles.cardDescription}>{t.appearance.description}</p>
          </div>
          <div style={styles.settingGroup}>
            <div style={styles.settingItem}>
              <label style={styles.settingLabel}>
                <div style={styles.labelContent}>
                  <span style={styles.labelTitle}>
                    {t.appearance.themeMode}
                  </span>
                  <span style={styles.labelDescription}>
                    {t.appearance.themeDescription}
                  </span>
                </div>
              </label>
              <div style={styles.themeToggle}>
                <button
                  style={{
                    ...styles.themeButton,
                    ...(theme === "light" ? styles.themeButtonActive : {}),
                  }}
                  onClick={() => setTheme("light")}
                >
                  <Sun size={isMobile ? 16 : 18} />
                  {t.appearance.light}
                </button>
                <button
                  style={{
                    ...styles.themeButton,
                    ...(theme === "dark" ? styles.themeButtonActive : {}),
                  }}
                  onClick={() => setTheme("dark")}
                >
                  <Moon size={isMobile ? 16 : 18} />
                  {t.appearance.dark}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedSettingsPage;